import React, { useState, useCallback, useRef } from "react";
import { createContext } from "use-context-selector";
import {
    saveSessionAnswersAPI,
    startOrResumeQuestionnaireSessionAPI,
    completeQuestionnaireSessionAPI
} from "../../Services/api";
import { get, isEqual } from "lodash";
import { 
    processEvent, 
    shouldSaveImmediately,
    getDebounceDelay,
    AnswerEvents,
} from "../../Components/Questionnaires/AnswerEventSystem";

/**
 * Questionnaire Session Context
 * Handles session-related state and functionality
 */
export const QuestionnaireSessionReactContext = createContext({
    // Session state
    questionnaireSession: null,
    sessionQuestions: [],
    finishSessionFlag: false,
    currentQuestionIndex: 0,
    isSessionCompleted: false,

    // Session functions
    initQuestionnaireSession: async ({signDocId}) => {},
    finishSession: async () => {},
    processAnswerEvent: (eventType, eventData, questionIndex) => {},
    cancelFinishExam: () => {},
    handlePreviousQuestion: async () => {},
    handleNextQuestion: async () => {},
    navigateToQuestion: async ({nextQuestionIndex}) => {},
});

// Context Provider Component
export const QuestionnaireSessionReactContextProvider = ({ children }) => {
    // Session state
    const [questionnaireSession, setQuestionnaireSession] = useState({});
    const [sessionQuestions, setSessionQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isSessionCompleted, setIsSessionCompleted] = useState(false);
    const [finishSessionFlag, setFinishSessionFlag] = useState(false);
    
    // Save timeouts for debounced questions (indexed by question index)
    const saveTimeouts = useRef({});

    /**
     * Clear all session data
     */
    const cleanUpSessionData = () => {
        // Clear all pending save timeouts
        Object.values(saveTimeouts.current).forEach(timeout => {
            clearTimeout(timeout);
        });
        saveTimeouts.current = {};

        setQuestionnaireSession({});
        setSessionQuestions([]);
        setCurrentQuestionIndex(0);
        setIsSessionCompleted(false);
        setFinishSessionFlag(false);
    };

    /**
     * Initialize questionnaire session
     * @param {object} params - Parameters object
     * @param {string} params.signDocId - SignDoc ID
     * @returns {Promise<object>} - Current questionnaire session
     */
    const initQuestionnaireSession = async ({ signDocId }) => {
        cleanUpSessionData();
        try {
            const currentQuestionnaireSession = await startOrResumeQuestionnaireSessionAPI({ signDocId });
            setQuestionnaireSession(currentQuestionnaireSession);

            let serverQuestions = get(currentQuestionnaireSession, 'questionnaireSession.questions', []);
            
            // Process each question to load existing answers using the event system
            const processedQuestions = serverQuestions.map(question => {
                return processEvent(AnswerEvents.ANSWER_LOADED, question, question);
            });
            
            setSessionQuestions(processedQuestions);

            return currentQuestionnaireSession;
        } catch (error) {
            console.error("Error initializing questionnaire session:", error);
        }
    };

    /**
     * Process an answer event using the event-driven system
     * @param {string} eventType - The type of event (from AnswerEvents)
     * @param {object} eventData - The event data
     * @param {number} [questionIndex] - Optional question index, defaults to current
     */
    const processAnswerEvent = useCallback((eventType, eventData, questionIndex = currentQuestionIndex) => {
        const question = sessionQuestions[questionIndex];
        if (!question) {
            console.warn(`No question found at index ${questionIndex}`);
            return;
        }
        
        const updatedQuestion = processEvent(eventType, question, eventData);
        const hasChanged = !isEqual(question, updatedQuestion);
        
        if (hasChanged) {
            updateQuestionInState(updatedQuestion, questionIndex);
            const questionType = get(question, 'type');
            scheduleSave(updatedQuestion, questionType, questionIndex);
        }
    }, [sessionQuestions, currentQuestionIndex]);

    /**
     * Update a specific question in the session state
     * @param {object} updatedQuestion - The updated question object
     * @param {number} questionIndex - The index of the question to update
     */
    const updateQuestionInState = useCallback((updatedQuestion, questionIndex) => {
        setSessionQuestions(prevQuestions => {
            const newQuestions = [...prevQuestions];
            newQuestions[questionIndex] = updatedQuestion;
            return newQuestions;
        });
    }, []);

    /**
     * Save answer for a specific question
     * @param {object} question - The question to save
     */
    const saveAnswer = useCallback(async (question) => {
        try {
            await saveSessionAnswersAPI({
                signDocId: get(questionnaireSession, 'SignDocId'),
                userSignDocId: get(questionnaireSession, 'id'),
                questionId: get(question, 'id'),
                answers: get(question, 'userAnswers', []),
                textResponse: get(question, 'textResponse', ''),
                ratingValue: get(question, 'ratingValue', null),
            });
        } catch (error) {
            console.error('Error saving answer for question:', error);
        }
    }, [questionnaireSession]); 

    /**
     * Schedule save based on question type configuration
     * @param {object} question - The question to save
     * @param {string} questionType - The type of question
     * @param {number} questionIndex - The index of the question
     */
    const scheduleSave = useCallback((question, questionType, questionIndex) => {
        if (shouldSaveImmediately(questionType)) {
            saveAnswer(question);
        } else {
            const debounceDelay = getDebounceDelay(questionType);
            if (debounceDelay) {
                // Clear existing timeout for this question
                if (saveTimeouts.current[questionIndex]) {
                    clearTimeout(saveTimeouts.current[questionIndex]);
                }
                
                // Set new timeout
                saveTimeouts.current[questionIndex] = setTimeout(() => {
                    saveAnswer(question);
                    delete saveTimeouts.current[questionIndex];
                }, debounceDelay);
            }
        }
    }, [saveAnswer]);

    /**
     * Navigate to a specific question
     * @param {object} params - Parameters object
     * @param {number} params.nextQuestionIndex - Index of the question to navigate to
     */
    const navigateToQuestion = async ({ nextQuestionIndex }) => {
        // Save current question before navigating
        if (sessionQuestions.length > 0 && currentQuestionIndex >= 0) {
            const currentQuestion = sessionQuestions[currentQuestionIndex];
            await saveAnswer(currentQuestion);
        }

        if (nextQuestionIndex >= sessionQuestions.length) {
            setFinishSessionFlag(true);
        } else if (nextQuestionIndex >= 0) {
            setCurrentQuestionIndex(nextQuestionIndex);
        }
    };

    /**
     * Cancel finish exam action
     */
    const cancelFinishExam = () => {
        setFinishSessionFlag(false);
    };

    /**
     * Finish the questionnaire session
     * @returns {Promise<object>} - Session with score
     */
    const finishSession = async () => {
        // Save the final answer before completing
        if (sessionQuestions.length > 0 && currentQuestionIndex >= 0) {
            const currentQuestion = sessionQuestions[currentQuestionIndex];
            await saveAnswer(currentQuestion);
        }

        let newSessionWithScore = await completeQuestionnaireSessionAPI({
            userSignDocId: get(questionnaireSession, 'id'), 
            signDocId: get(questionnaireSession, 'SignDocId')
        });
        setQuestionnaireSession(newSessionWithScore);
        return newSessionWithScore;
    };

    /**
     * Navigate to previous question
     */
    const handlePreviousQuestion = async () => {
        await navigateToQuestion({ nextQuestionIndex: currentQuestionIndex - 1 });
    };

    /**
     * Navigate to next question
     */
    const handleNextQuestion = async () => {
        await navigateToQuestion({ nextQuestionIndex: currentQuestionIndex + 1 });
    };

    const value = {
        // Session state
        sessionQuestions,
        questionnaireSession,
        currentQuestionIndex,
        isSessionCompleted,
        finishSessionFlag,

        // Session functions
        initQuestionnaireSession,
        processAnswerEvent,
        cancelFinishExam,
        finishSession,
        handlePreviousQuestion,
        handleNextQuestion,
        navigateToQuestion,
    };

    return (
        <QuestionnaireSessionReactContext.Provider value={value}>
            {children}
        </QuestionnaireSessionReactContext.Provider>
    );
}; 